import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../i18n/useTranslation';

const DatePicker = ({ value, onChange, min, className = '', placeholder }) => {
  const { language } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value) {
      const date = new Date(value);
      return new Date(date.getFullYear(), date.getMonth(), 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });
  const [currentYear, setCurrentYear] = useState(() => {
    if (value) {
      return new Date(value).getFullYear();
    }
    return new Date().getFullYear();
  });
  const pickerRef = useRef(null);
  const isRTL = language === 'ar';
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [availableDates, setAvailableDates] = useState(new Set());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch available slots for the current month
  useEffect(() => {
    if (!isOpen) return;

    const fetchAvailableDates = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        // Get all dates in the month
        const dates = [];
        for (let day = 1; day <= lastDay.getDate(); day++) {
          const date = new Date(year, month, day);
          const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          dates.push(dateStr);
        }

        // Fetch slots for each date
        const availableSet = new Set();
        await Promise.all(
          dates.map(async (dateStr) => {
            try {
              const response = await fetch(`${API_URL}/bookings/slots?date=${dateStr}`);
              if (response.ok) {
                const slots = await response.json();
                // Check if there are any available slots
                const hasAvailable = slots.some(slot => slot.isAvailable);
                if (hasAvailable) {
                  availableSet.add(dateStr);
                }
              }
            } catch (error) {
              // Silently fail for individual dates
            }
          })
        );
        setAvailableDates(availableSet);
      } catch (error) {
        // Silently fail
      }
    };

    fetchAvailableDates();
  }, [isOpen, currentMonth]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    // Calculate date range: 1 month ago to 1 year ahead
    const today = new Date();
    const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const oneYearAhead = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
    oneYearAhead.setMonth(oneYearAhead.getMonth() + 1); // Add 1 more month
    oneYearAhead.setDate(0); // Last day of that month

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      // Only include dates within the allowed range
      if (date >= oneMonthAgo && date <= oneYearAhead) {
        days.push(date);
      } else {
        days.push(null);
      }
    }
    return days;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (date) => {
    if (!date || !value) return false;
    const selected = new Date(value);
    // Normalize dates to compare only date part
    return (
      date.getDate() === selected.getDate() &&
      date.getMonth() === selected.getMonth() &&
      date.getFullYear() === selected.getFullYear()
    );
  };

  const isPast = (date) => {
    if (!date || !min) return false;
    const minDate = new Date(min);
    const checkDate = new Date(date);
    minDate.setHours(0, 0, 0, 0);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < minDate;
  };

  const handleDateClick = (date) => {
    if (!date || isPast(date)) return;
    // Format date as YYYY-MM-DD without timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    onChange(`${year}-${month}-${day}`);
    setIsOpen(false);
  };

  const canGoToPreviousMonth = () => {
    const today = new Date();
    const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    return currentMonthStart > oneMonthAgo;
  };

  const canGoToNextMonth = () => {
    const today = new Date();
    const oneYearAhead = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
    oneYearAhead.setMonth(oneYearAhead.getMonth() + 1);
    oneYearAhead.setDate(0);
    const nextMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    return nextMonthStart <= oneYearAhead;
  };

  const goToPreviousMonth = () => {
    if (!canGoToPreviousMonth()) return;
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    setCurrentMonth(newMonth);
    setCurrentYear(newMonth.getFullYear());
  };

  const goToNextMonth = () => {
    if (!canGoToNextMonth()) return;
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    setCurrentMonth(newMonth);
    setCurrentYear(newMonth.getFullYear());
  };

  const monthNames = language === 'ar'
    ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const dayNames = language === 'ar'
    ? ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    // Always use Gregorian calendar, but with Arabic month names if Arabic is selected
    const monthNames = language === 'ar'
      ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
      : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = language === 'ar'
      ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    const day = dayNames[date.getDay()];
    const month = monthNames[date.getMonth()];
    const dayNum = date.getDate();
    const year = date.getFullYear();
    
    return language === 'ar' 
      ? `${day}، ${dayNum} ${month} ${year}`
      : `${day}, ${month} ${dayNum}, ${year}`;
  };

  const days = getDaysInMonth();

  return (
    <div className="relative" ref={pickerRef} dir={isRTL ? 'rtl' : 'ltr'}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left bg-white flex items-center justify-between ${className}`}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {value ? formatDisplayDate(value) : placeholder || 'Select date'}
        </span>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      {isOpen && (
        <>
          {isMobile ? (
            /* Mobile: Card-based Date Picker */
            <div className={`absolute top-full ${isRTL ? 'right-0' : 'left-0'} mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-200 p-3 z-50 w-full max-h-[70vh] overflow-y-auto`} dir={isRTL ? 'rtl' : 'ltr'}>
              {/* Year Navigation */}
              <div className={`flex items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {canGoToPreviousMonth() ? (
                  <button
                    onClick={() => {
                      const today = new Date();
                      const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                      if (currentYear > oneMonthAgo.getFullYear() || 
                          (currentYear === oneMonthAgo.getFullYear() && currentMonth.getMonth() > oneMonthAgo.getMonth())) {
                        const newYear = currentYear - 1;
                        setCurrentYear(newYear);
                        setCurrentMonth(new Date(newYear, currentMonth.getMonth(), 1));
                      }
                    }}
                    className="p-2 rounded-lg transition hover:bg-gray-100"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M15 19l-7-7 7-7"} />
                    </svg>
                  </button>
                ) : (
                  <div className="w-10"></div>
                )}
                <h3 className="text-base font-semibold text-gray-900">
                  {currentYear}
                </h3>
                {canGoToNextMonth() ? (
                  <button
                    onClick={() => {
                      const today = new Date();
                      const oneYearAhead = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
                      oneYearAhead.setMonth(oneYearAhead.getMonth() + 1);
                      oneYearAhead.setDate(0);
                      if (currentYear < oneYearAhead.getFullYear() || 
                          (currentYear === oneYearAhead.getFullYear() && currentMonth.getMonth() < oneYearAhead.getMonth())) {
                        const newYear = currentYear + 1;
                        setCurrentYear(newYear);
                        setCurrentMonth(new Date(newYear, currentMonth.getMonth(), 1));
                      }
                    }}
                    className="p-2 rounded-lg transition hover:bg-gray-100"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M9 5l7 7-7 7"} />
                    </svg>
                  </button>
                ) : (
                  <div className="w-10"></div>
                )}
              </div>

              {/* Month Navigation */}
              <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {canGoToPreviousMonth() ? (
                  <button
                    onClick={goToPreviousMonth}
                    className="p-2 rounded-lg transition hover:bg-gray-100"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M15 19l-7-7 7-7"} />
                    </svg>
                  </button>
                ) : (
                  <div className="w-10"></div>
                )}
                <h3 className="text-lg font-semibold text-gray-900">
                  {monthNames[currentMonth.getMonth()]}
                </h3>
                {canGoToNextMonth() ? (
                  <button
                    onClick={goToNextMonth}
                    className="p-2 rounded-lg transition hover:bg-gray-100"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M9 5l7 7-7 7"} />
                    </svg>
                  </button>
                ) : (
                  <div className="w-10"></div>
                )}
              </div>

              {/* Date Cards - 2 per row */}
              <div className="grid grid-cols-2 gap-2">
                {days.filter(date => date !== null).map((date, index) => {
                  const past = isPast(date);
                  const today = isToday(date);
                  const selected = isSelected(date);
                  const dayOfWeek = date.getDay();
                  const dayName = dayNames[dayOfWeek];
                  
                  // Check if this date has available bookings
                  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  const hasAvailable = availableDates.has(dateStr);

                  return (
                    <button
                      key={date.toISOString()}
                      type="button"
                      onClick={() => handleDateClick(date)}
                      disabled={past}
                      className={`
                        p-3 rounded-lg border-2 transition-all text-left ${isRTL ? 'text-right' : 'text-left'}
                        ${past
                          ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                          : selected
                          ? 'bg-blue-600 border-blue-600 text-white shadow-lg'
                          : hasAvailable
                          ? 'bg-green-50 border-green-400 text-green-700 hover:bg-green-100 hover:border-green-500'
                          : today
                          ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                        }
                      `}
                    >
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold opacity-75 mb-1">{dayName}</span>
                        <span className="text-lg font-bold">{date.getDate()}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Desktop: Traditional Calendar Grid */
            <div className={`absolute top-full ${isRTL ? 'right-0' : 'left-0'} mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-200 p-4 z-50 min-w-[300px]`} dir={isRTL ? 'rtl' : 'ltr'}>
              <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {canGoToPreviousMonth() ? (
                  <button
                    onClick={goToPreviousMonth}
                    className="p-2 rounded-lg transition hover:bg-gray-100"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M15 19l-7-7 7-7"} />
                    </svg>
                  </button>
                ) : (
                  <div className="w-10"></div>
                )}
                <h3 className="text-lg font-semibold text-gray-900">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h3>
                {canGoToNextMonth() ? (
                  <button
                    onClick={goToNextMonth}
                    className="p-2 rounded-lg transition hover:bg-gray-100"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M9 5l7 7-7 7"} />
                    </svg>
                  </button>
                ) : (
                  <div className="w-10"></div>
                )}
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {days.map((date, index) => {
                  if (!date) {
                    return <div key={`empty-${index}`} className="aspect-square" />;
                  }

                  const past = isPast(date);
                  const today = isToday(date);
                  const selected = isSelected(date);
                  
                  // Check if this date has available bookings
                  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  const hasAvailable = availableDates.has(dateStr);

                  return (
                    <button
                      key={date.toISOString()}
                      type="button"
                      onClick={() => handleDateClick(date)}
                      disabled={past}
                      className={`
                        aspect-square p-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center
                        ${past
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : selected
                          ? 'bg-blue-600 text-white shadow-lg scale-105'
                          : hasAvailable
                          ? 'bg-green-100 border-2 border-green-400 text-green-700 hover:bg-green-200'
                          : today
                          ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                        }
                      `}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DatePicker;

